import React, { useCallback, useRef, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useSalvaCampo } from '../../utils/salvaCampo';
import { messaggioDa } from '../../utils/notifiche';

/**
 * Il disegno di un campo di scheda clinica, e le due regole che lo
 * accompagnano.
 *
 * STA IN UN FILE SUO perche' lo usano la procedura e il profilo del
 * paziente: la stessa scheda si compila in due posti e deve avere lo
 * stesso aspetto e le stesse regole. Due copie avrebbero smesso di
 * somigliarsi al primo campo nuovo, e un campo che si comporta in un modo
 * di qua e in un altro di la' e' il genere di differenza che si scopre da
 * un dato scritto male.
 */

export const chiave = (campoId, riga) => `${campoId}|${riga}`;

/* TOGLIERE UNA RIPETIZIONE, A SCHERMO, COME LA TOGLIE IL SERVER: via la
   riga dai campi di QUELLA sezione, le righe dopo scalano di uno, tutto
   il resto non si tocca. Si rifa' qui invece di ricaricare la scheda,
   perche' ricaricando si perderebbe quello che e' scritto e non ancora
   salvato nelle altre righe. Fino al 23 settembre 2026 il server
   cancellava la riga in tutta la compilazione: togliere la prima
   ripetizione cancellava anche ogni risposta delle sezioni normali. */
export const togliRipetizione = (valori, sezione, riga) => {
    const campi = new Set((sezione.campi || []).map((c) => String(c.campo_id)));
    const esito = {};
    for (const [k, v] of Object.entries(valori)) {
        const [campo, r] = k.split('|');
        const n = Number(r);
        if (!campi.has(campo)) { esito[k] = v; continue; }
        if (n === riga) continue;
        esito[chiave(campo, n > riga ? n - 1 : n)] = v;
    }
    return esito;
};

/* SALVATA UNA SCHEDA, L'AVVISO DEL PAZIENTE PUO' ESSERE CAMBIATO.
   La fascia rossa in cima alla scheda del paziente si ricava dalle
   compilazioni, e una spunta tolta qui la deve spegnere subito: lasciarla
   fino al prossimo F5 vuol dire mostrare una controindicazione che il
   medico ha appena smentito, che e' peggio di non mostrarla.
   Un evento e non un pacco di funzioni passate di mano in mano: fra chi
   salva e chi disegna la fascia ci sono due componenti che della cosa non
   sanno niente e non devono impararla. */
export { annunciaSchedaSalvata } from '../../utils/annunci';

/* Come si confrontano due valori di un campo: il vuoto e' uno solo
   (null, undefined, ''), una spunta e' «true» o «false». */
const normale = (v) => (v === null || v === undefined ? '' : String(v));

/**
 * I CAMPI DI UNA SCHEDA SI SALVANO DA SOLI, all'uscita dal campo (regola
 * di Alex del 24 settembre 2026, in CLAUDE.md; schermate decise da Alex lo
 * stesso giorno). Una copia sola per il profilo e per la procedura, sopra
 * il meccanismo dell'anamnesi (utils/salvaCampo.js).
 *
 * `scriviUno(campoId, riga, valore)` manda UN valore. Il server scrive
 * campo per campo e controlla il singolo valore (tipo, plausibilita'),
 * non la scheda intera: mandarne uno alla volta non cambia le regole.
 *
 * UN CAMPO CHE NON E' CAMBIATO NON SI MANDA: si confronta con l'ultimo
 * valore letto o salvato (`fotografa` lo prende quando la scheda si carica
 * o una ripetizione si toglie). Il server fa lo stesso per conto suo.
 *
 * `gruppo` separa le schede di una stessa procedura (lo stesso campo puo'
 * stare in due schede) e arriva a `scriviUno` come quarto argomento.
 */
export function useCampiAutomatici(scriviUno, onSalvato) {
    const salvati = useRef({});
    const [errori, setErrori] = useState({});
    const k = (gruppo, campoId, riga) => `${gruppo}#${chiave(campoId, riga)}`;
    const scrivi = useCallback(async (kk, v) => {
        try {
            await scriviUno(v.campo_id, v.riga, v.valore, v.gruppo);
            salvati.current[kk] = normale(v.valore);
            setErrori((p) => ({ ...p, [kk]: null }));
        } catch (e) {
            setErrori((p) => ({ ...p, [kk]: messaggioDa(e, 'il salvataggio non è riuscito') }));
            throw e;
        }
    }, [scriviUno]);
    const { stato, salva, nonSalvati } = useSalvaCampo(scrivi, onSalvato);

    /* I valori appena letti (o dopo aver tolto una ripetizione) sono quelli
       «salvati»: il confronto parte da li'. Nella procedura un gruppo per
       scheda, perche' lo stesso campo puo' stare in due schede. */
    const fotografa = useCallback((valori, gruppi = ['']) => {
        const f = {};
        for (const g of gruppi) {
            for (const [kv, v] of Object.entries(valori || {})) f[`${g}#${kv}`] = normale(v);
        }
        salvati.current = f;
    }, []);

    const esci = (campoId, riga, valore, gruppo = '') => {
        const kk = k(gruppo, campoId, riga);
        if (normale(valore) === (salvati.current[kk] ?? '')) return;      // non e' cambiato
        salva(kk, { campo_id: campoId, riga, valore, gruppo });
    };
    const di = (campoId, riga, gruppo = '') => {
        const kk = k(gruppo, campoId, riga);
        return { stato: stato[kk], errore: errori[kk] };
    };
    const riprova = (campoId, riga, valore, gruppo = '') =>
        salva(k(gruppo, campoId, riga), { campo_id: campoId, riga, valore, gruppo });

    return { esci, di, riprova, fotografa, nonSalvati };
}


/* NESSUNA SEGNALAZIONE «FUORI RIFERIMENTO», dal 21 settembre 2026.
   C'era: bordo giallo e la riga «riferimento 90-130 mmHg» sotto il
   campo. Confrontare un valore del paziente con un intervallo di
   normalita' e segnalare lo scostamento e' fornire informazioni per una
   decisione clinica (MDR, regola 11): RIMEDICHUB registra, non giudica.
   Il server non manda piu' riferimento_min/max, e questa schermata non
   li legge nemmeno se arrivassero: la rimozione qui e' la guardia vera,
   finche' il vincolo nel database non chiude anche la porta di dietro.
   Restano i limiti di plausibilita', che il server applica salvando. */

export const Campo = ({ campo, riga, valore, onChange, bloccato, onEsci, salvataggio, onRiprova }) => {
    const id = `c${campo.campo_id}r${riga}`;

    /* La descrizione della risposta scelta: per i prodotti infusionali e'
       la controindicazione, e si vede solo quando quel prodotto e' stato
       scelto - prima sarebbe rumore, dopo sarebbe tardi. */
    const scelta = (campo.opzioni || []).find((o) => o.valore === valore);

    const comune = { id, disabled: bloccato, onChange: (e) => onChange(e.target.value) };
    /* Il testo si salva quando si esce dal campo; una scelta o una spunta
       appena cambia, perche' li' «uscire» non esiste. */
    const testo = onEsci ? { ...comune, onBlur: (e) => onEsci(e.target.value) } : comune;
    const scegli = onEsci
        ? { ...comune, onChange: (e) => { onChange(e.target.value); onEsci(e.target.value); } }
        : comune;
    const st = salvataggio?.stato;

    return (
        <div className="sp-campo" data-salva-da-solo>
            <label htmlFor={id}>
                {campo.nome}
                {campo.unita && <span className="sp-unita"> ({campo.unita})</span>}
                {st === 'in_corso' && <span className="sp-campo-stato"> salvataggio…</span>}
                {st === 'salvato' && <span className="sp-campo-ok"> ✓ salvato</span>}
            </label>

            {campo.tipo === 'testo_lungo' ? (
                <textarea rows={3} value={valore ?? ''} {...testo} />
            ) : campo.tipo === 'scelta_singola' ? (
                <select value={valore ?? ''} {...scegli}>
                    <option value="">—</option>
                    {(campo.opzioni || []).map((o) => (
                        <option key={o.valore} value={o.valore}>{o.etichetta}</option>
                    ))}
                </select>
            ) : campo.tipo === 'spunta' ? (
                <input type="checkbox" checked={valore === true || valore === 'true'}
                       disabled={bloccato}
                       onChange={(e) => { onChange(e.target.checked); if (onEsci) onEsci(e.target.checked); }} />
            ) : campo.tipo === 'data' ? (
                <input type="date" value={(valore ?? '').toString().slice(0, 10)} {...testo} />
            ) : campo.tipo === 'numero' ? (
                <input type="number" step="any" value={valore ?? ''} {...testo} />
            ) : (
                <input type="text" value={valore ?? ''} {...testo} />
            )}

            {st === 'non_salvato' && (
                <div className="sp-campo-errore" role="alert">
                    <span>Non salvato: {salvataggio?.errore || 'il salvataggio non è riuscito'}. Il valore è ancora nel campo.</span>
                    {onRiprova && <button type="button" onClick={onRiprova}>Riprova</button>}
                </div>
            )}
            {campo.aiuto && <small className="sp-aiuto">{campo.aiuto}</small>}
            {scelta?.descrizione && (
                <p className="sp-avviso">
                    <AlertTriangle size={13} aria-hidden="true" /> {scelta.descrizione}
                </p>
            )}
        </div>
    );
};

